import { useEffect, useCallback, useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import { Grid, Container } from 'theme-ui';
import debounce from 'lodash/debounce';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import CategoriesProductsCount from '@features/categories/components/CategoriesProductsCount/CategoriesProductsCount';
import CategoriesProgressLine from '@features/categories/components/CategoriesProgressLine/CategoriesProgressLine';
import CategoriesMobileBottomPanel from '@features/categories/components/CategoriesMobileBottomPanel/CategoriesMobileBottomPanel';
import CategoriesShortcutFilters from '@features/categories/components/CategoriesShortcutFilters/CategoriesShortcutFilters';
import CategoriesSortSelect from '@features/categories/components/CategoriesSortSelect/CategoriesSortSelect';
import styles from '@features/categories/components/CategoriesListing/CategoriesListing.styles';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import FilterUnselected from '@components/icons/components/FilterUnselected';
import { IInfoCardWithMedia } from '@components/typescript';
import FiltersStore from '@features/categories/mobx/stores/FiltersStore';
import ProductList from '@components/ProductList/ProductList';
import { PRODUCTS_RENDER_COUNTS } from '@features/categories/const';
const DynamicCategoriesSelectedFilters = dynamic(() => import('@features/categories/components/CategoriesSelectedFilters/CategoriesSelectedFilters'), {
  ssr: false
});
type PropsType = {
  categoryId: number;
  categoryName: string;
  infoCards?: IInfoCardWithMedia[];
  filtersStore: FiltersStore;
};
const CategoriesListing = ({
  categoryId,
  categoryName,
  infoCards = [],
  filtersStore
}: PropsType) => {
  const {
    t
  } = useTranslation(['common', 'filters']);
  const listingRef = useRef<HTMLDivElement>(null);
  const [shouldResetPagination, setResetPagination] = useState(false);
  const {
    pagination,
    products,
    sorting,
    toggleAside,
    setActiveFilterView,
    setAllFiltersViewActive,
    fetchAndPopulateStoreWithData,
    loadMore,
    loadPrevious,
    filterValues,
    sortingValue,
    arrangementProducts,
    getArrangementProductProducts,
    areFiltersSameAsInitial,
    isSortingSameAsInitial,
    setCurrentFiltersAsInitial,
    setCurrentSortingAsInitial,
    fetchTrigger,
    setFetchTrigger
  } = filtersStore;
  const {
    setOffset
  } = pagination;
  const router = useRouter();
  const getNumberNextPage = () => Math.ceil((pagination.offset + pagination.productsToRender) / PRODUCTS_RENDER_COUNTS) + 1;
  const getNumberPreviousPage = () => Math.ceil(pagination.offset / PRODUCTS_RENDER_COUNTS);
  const incrementFetchTrigger = (resetPagination: boolean) => {
    setResetPagination(resetPagination);
    setFetchTrigger(fetchTrigger + 1);
  };
  const debouncedSetFetchTrigger = useCallback(debounce(incrementFetchTrigger, 1500), [fetchTrigger]);
  const setFilterView = filterName => {
    console.log('setFilterView', setFilterView);
    setActiveFilterView(filterName);
    setAllFiltersViewActive(true);
    setOffset(0);
    debouncedSetFetchTrigger(true);
  };
  const seeMoreElement = useRef<HTMLAnchorElement>(null);
  const scrollToNewProducts = () => {
    if (seeMoreElement?.current && window.innerHeight) {
      seeMoreElement.current.blur();
      const windowHalfHeight = Math.round(window.innerHeight / 2);
      const scrollTop = Math.abs(seeMoreElement.current.offsetTop - windowHalfHeight);
      if (scrollTop < window.scrollY) {
        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  };
  const handleShowMoreButton = () => {
    loadMore(categoryId, getNumberNextPage() || undefined);
    if (!filtersStore.hashPageQuery) scrollToNewProducts();
  };
  const handleShowPreviousButton = () => {
    loadPrevious(categoryId, getNumberPreviousPage() || undefined);
    if (!filtersStore.hashPageQuery) scrollToNewProducts();
  };
  useEffect(() => {
    if (fetchTrigger > 0) {
      fetchAndPopulateStoreWithData(categoryId, t('sort'), {
        resetPagination: shouldResetPagination
      });
      setResetPagination(false);
    }
  }, [fetchTrigger]);
  useEffect(() => {
    const areFiltersSameAsInitialValue = areFiltersSameAsInitial();
    if (fetchTrigger === 0 && (filterValues === null && areFiltersSameAsInitialValue || filterValues !== null && areFiltersSameAsInitialValue)) {
      return;
    }
    debouncedSetFetchTrigger(true);
  }, [filterValues]);
  useEffect(() => {
    const isSortingSameAsInitialValue = isSortingSameAsInitial();
    if (fetchTrigger === 0 && (sortingValue === null && isSortingSameAsInitialValue || sortingValue !== null && isSortingSameAsInitialValue)) {
      return;
    }
    debouncedSetFetchTrigger(true);
  }, [sortingValue]);
  const getNextPageUrl = () => {
    const currentPath = router.asPath;
    const hasQuery = currentPath.includes('?');
    const currentPage = parseInt(router.query.page as string, 10) || 1;
    const nextPage = getNumberNextPage() || currentPage + 1;
    return hasQuery ? currentPath.replace(`page=${currentPage}`, `page=${nextPage}`) : `${currentPath}${currentPath.includes('?') ? '&' : '?'}page=${nextPage}`;
  };
  const getPreviousPageUrl = () => {
    const currentPath = router.asPath;
    const hasQuery = currentPath.includes('?');
    const currentPage = parseInt(router.query.page as string, 10) || 1;
    const previousPage = getNumberPreviousPage() || currentPage - 1;
    return hasQuery ? currentPath.replace(`page=${pagination.page}`, `page=${previousPage}`) : `${currentPath}${currentPath.includes('?') ? '&' : '?'}page=${previousPage}`;
  };
  const getActualCount = () => Math.min(products.length, pagination.count);
  return <>
      <Container variant="fluid" sx={styles.listContainer} ref={listingRef}>
        <Grid columns={24} gap={0} sx={styles.filtersToSelectContainer} data-cy="PanelFilters">
          <div sx={styles.leftFilterColumn}>
            <Button size="small" variant="link" variantColor="dark" onClick={toggleAside} cy="VoxButtonFilters" addSx={styles.filterButton} addClassName="filter-button">
              <Icon size="24px" margin="0 16px 0 0">
                <FilterUnselected />
              </Icon>
              <span sx={styles.filterButtonText}>{t('allFilters', {
                ns: 'filters'
              })}</span>
            </Button>
            <CategoriesShortcutFilters filtersStore={filtersStore} />
          </div>
          <div sx={styles.rightFilterColumn}>
            {sorting && <div sx={styles.sortSelectWrapper}>
                <CategoriesSortSelect sorting={sorting} />
              </div>}
            <CategoriesProductsCount productsLength={pagination.count} align="center" />
          </div>
        </Grid>
        <Grid columns={24} gap={0} sx={styles.filtersSelectedContainer}>
          {/* @ts-expect-error - React 19 compatibility issue with next/dynamic types */}
          <DynamicCategoriesSelectedFilters filtersStore={filtersStore} />
        </Grid>
        <Grid columns={1} sx={styles.bottomRow}>
          {pagination.offset !== 0 && <Grid columns={1} sx={styles.buttonCenter}>
              <Link href={getPreviousPageUrl()} ref={seeMoreElement} onClick={e => {
            e.preventDefault();
            handleShowPreviousButton();
          }} sx={styles.seeMoreButton} data-cy="VoxButtonPaginationPrevious">
                <span>{t('seePrevious')}</span>
              </Link>
            </Grid>}
        </Grid>
        <Grid columns={24} sx={styles.productList}>
          <ProductList products={products} infoCards={infoCards} arrangementProducts={arrangementProducts} getArrangementProductProducts={getArrangementProductProducts} productsCount={products.length} category={categoryName} listingType={categoryName} listingId={categoryId} onProductClick={() => {
          setCurrentFiltersAsInitial();
          setCurrentSortingAsInitial();
          setFetchTrigger(0);
        }} />
        </Grid>
        <div sx={styles.filler} />
        <Grid columns={1} sx={styles.bottomRow}>
          <div sx={styles.progressLineSize}>
            <CategoriesProgressLine actualCount={getActualCount()} maxCount={pagination.count} />
          </div>
          {pagination.offset + pagination.productsToRender < pagination.count && <Grid columns={1} sx={styles.buttonCenter}>
              <Link href={getNextPageUrl()} ref={seeMoreElement} onClick={e => {
            e.preventDefault();
            handleShowMoreButton();
          }} sx={styles.seeMoreButton} data-cy="VoxButtonPaginationNext">
                <span>{t('seeMore')}</span>
              </Link>
            </Grid>}
        </Grid>
      </Container>
      <CategoriesMobileBottomPanel productsCount={pagination.count} filterToggle={toggleAside} onFilterButtonClick={setFilterView} listingRef={listingRef} />
    </>;
};
export default observer(CategoriesListing);